<template>
  <div v-if="!$rsGlobals.$isIFrame" class="header-page-title px-3 py-2">
    <div class="row m-0 align-items-center">
      <button
        v-if="backButtonConfig"
        class="title-back-button mr-3"
        :title="backButtonConfig.title"
        @click="backButtonConfig.handler()"
      >
        <i class="fas fa-arrow-left" />
      </button>

      <div
        v-else
        class="title-icon mr-3 d-flex align-items-center justify-content-center"
      >
        <img
          v-if="currentRoute?.meta?.customIcon"
          class="customSVG"
          :src="currentRoute.meta.customIcon"
        >
        <i
          v-else
          :class="`fal fa-${currentRoute?.meta?.icon}`"
        />
      </div>

      <div v-if="!breadcrumbs" class="flex-grow-1 d-flex align-items-center">
        <div
          v-for="(r, idx) in getRouteTree(currentRoute)"
          :key="r.name"
        >
          <RouterLink
            :id="`sub_${(r.name || r.path)}`"
            :class="{ 'crumbs px-1': idx > 0, 'mb-0 font-weight-bold crumb-parent': idx === 0 }"
            :to="{ name: r.name }"
            class="rs-text-white"
          >
            {{ getRouteTitle(r) }}
          </RouterLink>
          <i
            v-show="idx !== getRouteTree(currentRoute).length - 1"
            class="far fa-angle-right mx-2 crumbs rs-text-white"
          />
        </div>
      </div>

      <div v-else class="flex-grow-1 d-flex align-items-center">
        <div
          v-for="(crumb, idx) in breadcrumbs"
          :key="idx"
        >
          <RouterLink
            :id="`sub_${crumb.to.replace(/\//g, '_')}`"
            :class="{ 'crumbs px-1': idx > 0, 'mb-0 font-weight-bold crumb-parent': idx === 0 }"
            :to="crumb.to"
            class="rs-text-white"
            :title="crumb.title"
            :style="crumb.disabled ? 'pointer-events: none;' : ''"
          >
            {{ crumb.disableTruncation ? crumb.title : truncate(crumb.title, 20, 'plain') }}
          </RouterLink>
          <i
            v-show="idx !== breadcrumbs.length - 1"
            class="far fa-angle-right mx-2 crumbs rs-text-white"
          />
        </div>
      </div>

      <div class="flex-grow-1" />

      <RsButton
        v-if="$rsGlobals.$env.VUE_APP_ENV === 'development' && $rsGlobals.$currentUser.isSuperUser"
        hollow
        class="mr-2"
        icon="puzzle-piece"
        variant="white"
        @click="$router.push('/dev/form-components')"
      >
        Components
      </RsButton>

      <BDropdown
        v-if="$rsGlobals.$env.VUE_APP_ENV === 'development' && $rsGlobals.$currentUser.isSuperUser"
        id="ddl_dev"
        text="Dev"
        dropbottom
        menu-class="dev-ddl"
        class="shadow"
        no-caret
        variant="dark"
      >
        <BDropdownItem v-for="route in devRoutes" :key="route.name" @click="$router.push({ name: route.name })">
          {{ getRouteTitle(route) }}
        </BDropdownItem>
      </BDropdown>

      <RsDropdown
        v-if="$rsGlobals.$currentUser.isSuperUser"
        ref="suClientDropdown"
        plain
        dropdown
        right
        no-caret
        class="d-inline-block ml-3 su-client-button"
        :class="{ 'has-sources': sources.length > 0 }"
      >
        <template #btn-content>
          <RsButton id="btn_client_popover_target" variant="primary" class="shadow">
            <div class="d-flex align-items-center">
              <i class="far fa-building-user mr-1" />
              <div class="text-left">
                <div class="line-height-1">
                  {{ $rsGlobals.$currentUser.client.lwsCompanyLabel || $rsGlobals.$currentUser.client.name }}
                </div>
                <div class="line-height-1">
                  <small>{{ $rsGlobals.$currentUser.client.id }} - {{ $rsGlobals.$currentUser.client.clientKey }}</small>
                </div>
              </div>
            </div>
          </RsButton>
        </template>

        <div class="row mx-0 small py-2">
          <div class="col flex-grow-1">
            <div class="border-bottom mb-2 pb-2">
              <div class="d-flex pb-1">
                <div class="font-weight-bold">
                  ID:
                </div>
                <div class="flex-grow-1 pl-2">
                  {{ $rsGlobals.$currentUser.client.id }} - {{ $rsGlobals.$currentUser.client.clientKey }}
                </div>
              </div>
              <div class="d-flex">
                <div class="font-weight-bold">
                  <strong>Portfolio Type:</strong>
                </div>
                <div class="flex-grow-1 pl-2">
                  <span
                    class="rounded py-1 px-2 small font-weight-bold text-white"
                    :class="{
                      'rs-bg-secondary-light': $rsGlobals.$currentUser.client.portfolioType === 'mixed',
                      'rs-bg-info': $rsGlobals.$currentUser.client.portfolioType === 'multi-family',
                      'rs-bg-warning': $rsGlobals.$currentUser.client.portfolioType === 'single-family',
                    }"
                  >
                    {{ $rsGlobals.$currentUser.client.portfolioType.replace('-', ' ').toUpperCase() }}
                  </span>
                </div>
              </div>
            </div>

            <div v-if="typeof $rsGlobals.$currentUser.client.sfRestrictUnits === 'number'" class="d-flex pt-1 align-items-center">
              <i class="fas fa-lock mr-2 rs-text-error" /> SF Unit Restrictions ({{ $rsGlobals.$currentUser.client.sfRestrictUnits }})
            </div>

            <div v-if="$rsGlobals.$userHasAtLeastOneAccess('sites')" class="d-flex pt-1 align-items-center">
              <i class="mr-2 far fa-globe rs-text-secondary-light" />Websites
            </div>

            <div v-if="$rsGlobals.$userHasAtLeastOneAccess('standard_listings')" class="d-flex pt-1 align-items-center">
              <i class="mr-2 far fa-sync-alt rs-text-secondary" />Standard Listings
            </div>

            <div v-if="$rsGlobals.$userHasAtLeastOneAccess('promoted_listings')" class="d-flex pt-1 align-items-center">
              <i class="mr-2 far fa-fire rs-text-warning" />Promoted Listings
            </div>

            <div v-if="$rsGlobals.$currentUser.client.hasRentalsBundle" class="d-flex pt-1 align-items-center">
              <i class="mr-2 far fa-gift rs-text-info" />Infinity Bundle
            </div>

            <div v-if="$rsGlobals.$currentUser.client.tieredUnitCountConfig" class="d-flex pt-1 align-items-center" :style="{ position: 'relative' }">
              <i class="mr-2 far fa-building rs-text-warning" style="position: absolute; left: 2px; z-index: 1;" />
              <i class="mr-2 fas fa-feather" style="opacity: 0.15" />
              Multi-family Lite ({{ $rsGlobals.$currentUser.client.tieredUnitCountConfig }})
            </div>

            <div
              v-if="$rsGlobals.$currentUser.client.hasRentalsBundle && $rsGlobals.$currentUser.getClientSettingValue('infinity_source_reporting') === '1'"
              class="d-flex pt-1 align-items-center"
            >
              <i class="mr-2 far fa-bar-chart rs-text-info" />Infinity Source Reporting
            </div>

            <div v-if="$rsGlobals.$userHasAccess('tour_admins')" class="d-flex pt-1 align-items-center">
              <i class="mr-2 far fa-calendar-day rs-text-primary" />Tours
            </div>

            <div v-if="$rsGlobals.$userHasAtLeastOneAccess('floorplan_navigator')" class="d-flex pt-1 align-items-center">
              <i class="mr-2 fas fa-map-marked-alt rs-text-secondary-light" />FPN
            </div>

            <div v-if="$rsGlobals.$userHasAtLeastOneAccess('premium_reporting')" class="d-flex pt-1 align-items-center">
              <i class="mr-2 far fa-chart-line rs-text-error" />Market Insights
            </div>

            <div v-if="$rsGlobals.$currentClient.buildingStackClient?.status === 'enabled'" class="d-flex pt-1 align-items-center">
              <img src="/img/bstk/bstk-logo-sm.png" style="width: 18px;" class="mr-2" />
              Building Stack Integration
              <i
                v-if="$rsGlobals.$currentClient.buildingStackSyncSettings?.status === 'enabled'"
                v-b-tooltip.hover="'Syncing Properties & Units to Rentsync'"
                class="far fa-sync fa-fade ml-2 rs-text-primary"
                style="--fa-animation-duration: 3s; font-size: 1.2em"
              />
            </div>
          </div>

          <div v-if="sources.length > 0" class="col-6 mb-0 border-left">
            <label class="d-block border-bottom pb-2 mb-0">Active Import Sources</label>

            <div
              :style="{
                maxHeight: '300px',
                overflowY: 'auto',
              }"
              class="px-1"
            >
              <div v-for="(source, index) in sources" :key="source.id ?? index" class="pt-2 d-flex">
                <div class="mr-2">
                  <i class="fas fa-upload rs-text-secondary-light" />
                </div>
                <div class="flex-grow-1">
                  <div>
                    <span class="text-capitalize rs-text-secondary">{{ source.sourceName }}</span>
                    <em class="pl-1">[{{ source.sourceType }}]</em>
                  </div>
                  <div>
                    <i class="fal fa-clock mr-1 rs-text-primary" />
                    {{ source.lastSuccessful ? $rsGlobals.$utils.rsMedDateTime(source.lastSuccessful) : 'N/A' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RsDropdown>

      <button
        v-if="currentRoute?.meta?.reportingUrl"
        v-b-tooltip.hover
        class="title-offset-btn title-end-button ml-3"
        title="Reporting"
        @click="(currentRoute.meta.reportingUrl.startsWith('/') || currentRoute.meta.reportingUrl.startsWith('http')) && $router.push(currentRoute.meta.reportingUrl)"
      >
        <i class="far fa-chart-pie" />
      </button>

      <button
        v-if="showGuideControls && currentRoute?.meta?.infoUrl"
        id="helpguide"
        v-b-tooltip.hover
        class="title-offset-btn title-end-button ml-3"
        title="Help Guide"
      >
        <i class="far fa-info" />
      </button>

      <button
        v-if="showGuideControls"
        id="pendo-feedback-cta"
        v-b-tooltip.hover
        class="title-offset-btn btn-circle title-end-button ml-3"
        title="Feedback?"
      >
        <i class="far fa-comment-alt-edit mt-1 small" />
      </button>

      <button
        class="d-inline-block title-offset-btn btn-circle title-end-button resource-centre-button ml-3"
        @click="openRentsyncHelpCenter"
      >
        <i class="top-level-nav-item fas fa-question" />
      </button>

      <div class="d-inline-block ml-3" :class="{ impersonating: isImpersonating }">
        <i v-if="isImpersonating" class="far fa-exclamation-circle fa-beat-fade mx-2" />

        <RsDropdown
          ref="avatarDropdown"
          plain
          dropdown
          right
          no-caret
          class="d-inline-block title-offset-btn btn-circle title-end-button avatar-button"
          :class="{ superuser: $rsGlobals.$currentUser.isSuperUser }"
        >
          <template #btn-content>
            <div
              class="header-avatar rs-text-secondary d-flex align-items-center justify-content-center rs-bg-content-purple"
              @click="handleIsDropdownVisible"
            >
              {{ initials }}
            </div>
          </template>
          <div v-if="isImpersonating" class="rs-bg-warning p-3">
            <p class="mb-0 font-weight-bold">
              You are currently impersonating this user.
              <span style="cursor:pointer;text-decoration:underline;" @click="handleStopImpersonating">Stop Impersonating</span>
              to end impersonation.
            </p>
          </div>
          <RsPartialAvatarDropdown
            class="client-profile-dropdown"
            :visibility-watch="isAvatarDropdownVisible"
          />
        </RsDropdown>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { RouteConfig } from 'vue-router';
import appMixin from '@/mixins/app-mixin';
import RsPartialAvatarDropdown from '@/components/RsPartialAvatarDropdown.vue';
import { mapModuleActions, mapModuleGetters } from '@/shared/vuex-toolkit';
import { clientsDataSources, main } from '@/store/typed/typed-modules';
import devRoutes from '@/router/_dev';
import { IBreadcrumbConfig } from '@/shared';
import { getRouteMetaTitle } from '@/shared/helpers/route-meta-translations';
import { truncate } from '@/shared/helpers/truncate';
import app from '@/main';
import { storage } from '@/shared/helpers/storage';
import users from '@/shared/services/users';

export default defineComponent({
  name: 'RsPartialAppHeader',

  components: {
    RsPartialAvatarDropdown,
  },

  mixins: [ appMixin ],

  data() {
    return {
      devRoutes,
      isAvatarDropdownVisible: 0,
    };
  },

  computed: {
    ...mapModuleGetters(main, ['userSettings']),
    ...mapModuleGetters(clientsDataSources, { sources: 'list' }),

    showGuideControls(): boolean {
      return false; // Temporarily disable guides until we integrate a new provider
    },

    initials(): string {
      if (!this.$rsGlobals.$currentUser.firstName?.trim().length) {
        return this.$rsGlobals.$currentUser.email?.split('')[0].toUpperCase();
      }

      return `${this.$rsGlobals.$currentUser.firstName.split('')[0].toUpperCase()}${(this.$rsGlobals.$currentUser.lastName ?? '').split('')[0].toUpperCase()}`;
    },

    breadcrumbs(): IBreadcrumbConfig[] | null {
      const { breadcrumbs } = (this.currentRoute.meta || undefined) ?? {};
      return breadcrumbs ? breadcrumbs(this as unknown as Required<ReturnType<typeof app>>) : null;
    },

    currentRoute(): RouteConfig {
      return this.$route as RouteConfig;
    },

    isImpersonating(): boolean {
      return storage.get('currentUserSettings')?.impersonated;
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    getRouteTitle(route: { meta?: unknown }): string {
      return getRouteMetaTitle(this, route);
    },

    ...mapModuleActions(clientsDataSources, { getDataSources: 'get' }),

    truncate,

    handleIsDropdownVisible() {
      this.isAvatarDropdownVisible += 1;
    },

    init() {
      if (this.$rsGlobals.$userHasAccess('superuser')) {
        this.getDataSources({ parentId: Number(this.$rsGlobals.$currentClient.id) });
      }
    },

    handleLogout() {
      users.logout();
    },

    async handleStopImpersonating() {
      await users.stopImpersonating();
      window.location.reload();
    },

    openRentsyncHelpCenter() {
      window.open('https://help.rentsync.com/', '_blank');
    },
  },
});
</script>

<style lang='scss'>
@import '@/styles/_variables.scss';

.impersonating {
  background: $warning !important;
  border-radius: 20px;
  border: 3px solid $warning;
}

.header-avatar {
  width: 34px;
  height: 34px;
  border-radius:17px;
  font-weight: bold;
  line-height: 1;
  font-size: 16px;
}

.su-client-button {
  .rs-dropdown {
    &__menu {
      width: 260px !important;
      border: solid 1px $border-color;
    }
  }

  &.has-sources {
    .rs-dropdown {
      &__menu {
        width: 520px !important;
      }
    }
  }
}

.avatar-button {
  .rs-dropdown {
    &__menu {
      min-height: auto;
      max-height: 85vh;
      overflow-y: auto;
      padding: 0;
      width: 300px;
    }
  }
  &.superuser {
    .rs-dropdown {
      &__menu {
        width: 450px !important;
      }
    }
  }
}
</style>
